import css from '@emotion/css';
import styled from '@emotion/styled';
import { CalendarEventPill } from '__components/Kizen/EventPill';
import { usePillHover } from '../HoverContext';
import { useCallback, useMemo } from 'react';
import { Tooltip, useTooltip } from '@kizen/kds/Tooltip';
import { markdownToHtmlSync } from '@kizen/parse-md';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const ExternalCalendarEventPillWrapper = styled(CalendarEventPill)`
  margin-bottom: 5px;
  min-height: 21px;
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
  cursor: ${({ canEdit }) => (canEdit ? 'pointer' : 'default')};

  /* Override the left bar color with custom color */
  ${({ customColor }) =>
    customColor &&
    `
    & > div:first-of-type {
      background-color: ${customColor} !important;
    }
  `}

  & p {
    color: var(--color-font-primary) !important;
  }

  ${({ isPastDate }) =>
    isPastDate &&
    css`
      opacity: 0.6;
    `}

  ${({ isHovered }) =>
    isHovered
      ? css`
          background-color: var(--color-background-light);

          & .menu-button-wrapper {
            opacity: 1;
          }
        `
      : ''}
`;

const LinkWrapper = styled.a`
  &:hover {
    text-decoration: none;
  }
`;

const formatTimeStampForTooltip = (
  startTimestamp: number,
  endTimestamp: number,
  allDay: boolean,
  part: 'start' | 'end',
  t: TFunction
) => {
  const startDate = new Date(startTimestamp);
  const endDate = new Date(endTimestamp - (allDay ? 1 : 0)); // Subtract 1ms from end time for all-day events to display the correct end date

  if (part === 'start') {
    const dateString = startDate.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });

    const timeString = allDay
      ? t('All Day')
      : startDate.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        });

    return `${dateString} ${timeString}`;
  } else {
    const timeString = endDate.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });

    const duration = endTimestamp - startTimestamp;
    const durationHours = duration / (1000 * 60 * 60);
    if (durationHours <= 24) {
      if (allDay) {
        return '';
      }

      return timeString;
    }

    const dateString = endDate.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });

    return `${dateString} ${allDay ? t('All Day') : timeString}`;
  }
};

const sanitizeEmailForTooltip = (email?: string) => {
  if (!email) {
    return '';
  }

  // Calendar names are often emails, and attendees will usually be emails. Markdown will try to format
  // these as clickable URLs, which is wrong. Add an html comment to break up the pattern matching
  return email.replace('@', '<!-- -->@');
};

const formatAttendeesList = (attendees: string[]) => {
  if (attendees.length === 0) {
    return '';
  }

  if (attendees.length === 1) {
    return sanitizeEmailForTooltip(attendees[0]);
  }

  if (attendees.length === 2) {
    return `${sanitizeEmailForTooltip(attendees[0])} and ${sanitizeEmailForTooltip(attendees[1])}`;
  }

  if (attendees.length === 3) {
    return `${sanitizeEmailForTooltip(attendees[0])}, ${sanitizeEmailForTooltip(attendees[1])} and ${sanitizeEmailForTooltip(attendees[2])}`;
  }

  const othersCount = attendees.length - 3;

  return `${sanitizeEmailForTooltip(attendees[0])}, ${sanitizeEmailForTooltip(attendees[1])}, ${sanitizeEmailForTooltip(attendees[2])} and ${othersCount} other${othersCount > 1 ? 's' : ''}`;
};

export const getTooltipPosition = (
  tooltipPositionProps?: any,
  columnIndex?: number,
  totalColumns?: number
) => {
  return tooltipPositionProps
    ? tooltipPositionProps
    : columnIndex !== undefined && totalColumns !== undefined
      ? columnIndex >= totalColumns - 2
        ? 'left'
        : 'right'
      : 'top';
};

export const ExternalCalendarEventPill = (props: any) => {
  const {
    id,
    tooltipPosition: tooltipPositionProps,
    columnIndex,
    totalColumns,
    children,
    event,
  } = props;

  const { onPillHover, isHovered } = usePillHover();

  const handleHoverChange = useCallback(
    (hovered: boolean) => {
      onPillHover?.(id, hovered);
    },
    [id, onPillHover]
  );

  const { triggerProps, targetProps, showTooltip, tooltipProps } = useTooltip(
    true,
    handleHoverChange
  );

  const tooltipPosition = getTooltipPosition(
    tooltipPositionProps,
    columnIndex,
    totalColumns
  );

  const { t } = useTranslation();

  const parsedMarkDown = useMemo(() => {
    const title = `**${children}**`;
    const calendar = `${t('Calendar')}: ${sanitizeEmailForTooltip(event.calendarName) || t('Unknown')}`;
    const start = formatTimeStampForTooltip(
      event.originalEvent.start_time,
      event.originalEvent.end_time,
      event.originalEvent.all_day,
      'start',
      t
    );
    const end = formatTimeStampForTooltip(
      event.originalEvent.start_time,
      event.originalEvent.end_time,
      event.originalEvent.all_day,
      'end',
      t
    );
    const time = `${start}${end ? ` to ${end}` : ''}`;

    const attendees = event.originalEvent?.attendees?.length
      ? `${t('Attendees')}: ${formatAttendeesList(event.originalEvent.attendees)}`
      : '';

    const tooltipMarkdown = `${title}\n\n${calendar}\n\n${time}\n\n${attendees}`;

    return markdownToHtmlSync(tooltipMarkdown, { forceExternalLinks: true });
  }, [children, event, t]);

  const card = (
    <span {...triggerProps} {...targetProps}>
      {showTooltip && (
        <Tooltip
          position={tooltipPosition as any}
          interactive
          html={parsedMarkDown}
          {...tooltipProps}
        />
      )}
      <ExternalCalendarEventPillWrapper
        {...props}
        isHovered={isHovered(id)}
        canEdit={Boolean(props.url)}
        disableTooltip
      />
    </span>
  );

  if (props.url) {
    return (
      <LinkWrapper href={props.url} target="_blank" rel="noopener noreferrer">
        {card}
      </LinkWrapper>
    );
  } else {
    return card;
  }
};
